<script>
	import { onMount, onDestroy } from 'svelte';
	import { currentUser, conversations, selectedConversation, dashboardStats } from './store.js';
	import { conversationsAPI, statsAPI, authAPI, teamAPI, searchAPI } from './api.js';
	import { connect as rtConnect, disconnect as rtDisconnect } from './lib/realtime.js';
	import Icon from './lib/Icon.svelte';
	import Home from './Home.svelte';
	import Leads from './Leads.svelte';
	import AIAgents from './AIAgents.svelte';
	import Flows from './Flows.svelte';
	import Team from './Team.svelte';
	import Campaigns from './Campaigns.svelte';
	import Conversations from './Conversations.svelte';
	import ConversationDetail from './ConversationDetail.svelte';
	import Contacts from './Contacts.svelte';
	import Labels from './Labels.svelte';
	import AIProviders from './AIProviders.svelte';
	import KnowledgeBase from './KnowledgeBase.svelte';
	import Chatbots from './Chatbots.svelte';
	import Automations from './Automations.svelte';
	import Channels from './Channels.svelte';
	import Users from './Users.svelte';
	import Organizations from './Organizations.svelte';
	import SuperAdminPanel from './SuperAdminPanel.svelte';
	import Settings from './Settings.svelte';
	import Help from './Help.svelte';
	import Tutorials from './Tutorials.svelte';

	let activeView = $state('home');
	let loading = $state(true);
	// Kommo-style: icon rail by default, expandable on demand
	let collapsed = $state(true);
	// Mobile: the sidebar becomes an off-canvas drawer opened from the topbar
	let mobileNavOpen = $state(false);
	let userMenuOpen = $state(false);
	let searchQuery = $state('');
	let searchResults = $state(null);
	let searchOpen = $state(false);
	let searchTimer = null;
	let helpOpen = $state(false);

	// Email verification nudge
	let verifyBannerDismissed = $state(false);
	let resendingVerify = $state(false);
	async function resendVerification() {
		resendingVerify = true;
		try {
			const res = await authAPI.resendVerification();
			alert(res.message || 'Correo enviado');
		} catch (e) {
			alert(e.message || 'No se pudo enviar el correo');
		} finally {
			resendingVerify = false;
		}
	}

	// Detect impersonation session
	let impersonating = $state(null);
	function checkImpersonation() {
		try {
			const raw = localStorage.getItem('impersonating');
			if (!raw) { impersonating = null; return; }
			const data = JSON.parse(raw);
			if (Date.now() > data.expires_at) {
				localStorage.removeItem('impersonating');
				impersonating = null;
			} else {
				impersonating = data;
			}
		} catch { impersonating = null; }
	}
	function exitImpersonation() {
		localStorage.removeItem('access_token');
		localStorage.removeItem('refresh_token');
		localStorage.removeItem('user');
		localStorage.removeItem('impersonating');
		window.location.href = '/';
	}

	function onSearchInput() {
		searchOpen = true;
		clearTimeout(searchTimer);
		const q = searchQuery.trim();
		if (q.length < 2) { searchResults = null; return; }
		searchTimer = setTimeout(async () => {
			try {
				searchResults = await searchAPI.query(q);
			} catch (e) {
				searchResults = { error: e.message };
			}
		}, 250);
	}

	function pickSearchConversation(conv) {
		searchOpen = false;
		searchQuery = '';
		searchResults = null;
		setView('conversations');
		const target = ($conversations || []).find(c => c.id === conv.id);
		selectedConversation.set(target || conv);
	}

	function pickSearchLead() {
		searchOpen = false;
		searchQuery = '';
		searchResults = null;
		setView('leads');
	}

	function pickSearchContact() {
		searchOpen = false;
		searchQuery = '';
		searchResults = null;
		setView('contacts');
	}

	// Presence + mentions
	let presenceStatus = $state('online');
	let presenceMessage = $state('');
	let mentions = $state([]);
	let unreadMentions = $state(0);
	let mentionsOpen = $state(false);
	let presenceTimer = null;

	const PRESENCE_OPTS = [
		{ k: 'online',  label: 'En línea',     color: 'var(--success)' },
		{ k: 'busy',    label: 'Ocupado',      color: 'var(--danger)' },
		{ k: 'away',    label: 'Ausente',      color: 'var(--warning)' },
		{ k: 'offline', label: 'Desconectado', color: 'var(--text-3)' }
	];

	const navMain = [
		{ id: 'home',          label: 'Inicio',         icon: 'pie-chart' },
		{ id: 'leads',         label: 'Leads',          icon: 'kanban' },
		{ id: 'conversations', label: 'Bandeja',        icon: 'inbox', badgeKey: 'open_conversations' },
		{ id: 'contacts',      label: 'Contactos',      icon: 'users' },
		{ id: 'labels',        label: 'Etiquetas',      icon: 'tag' }
	];

	// `feature` = module key from the org's package; the item is hidden when
	// the module is disabled (backend enforces too — this is just UX).
	const navAI = [
		{ id: 'ai-agents',    label: 'AI Agents',       icon: 'sparkles', feature: 'ai_agents' },
		{ id: 'campaigns',    label: 'Campañas',        icon: 'send',     feature: 'campaigns' },
		{ id: 'flows',        label: 'Flujos',          icon: 'zap',      feature: 'salesbot' },
		{ id: 'chatbots',     label: 'Chatbots',        icon: 'bot',      feature: 'ai_agents' },
		{ id: 'knowledge',    label: 'Conocimiento',    icon: 'book-open' },
		{ id: 'ai-providers', label: 'Proveedores IA',  icon: 'plug' }
	];

	let userOrg = $derived($currentUser?.organization || null);
	let orgSuspended = $derived(
		!!userOrg?.is_suspended && $currentUser?.role !== 'super_admin'
	);
	let trialDaysLeft = $derived(
		userOrg?.trial_active ? userOrg.trial_days_left : null
	);

	let orgFlags = $derived($currentUser?.organization?.feature_flags || {});
	let visibleNavAI = $derived(
		$currentUser?.role === 'super_admin'
			? navAI
			: navAI.filter(i => !i.feature || orgFlags[i.feature] !== false)
	);

	const navSystem = [
		{ id: 'team',     label: 'Equipo',  icon: 'users' },
		{ id: 'channels', label: 'Canales', icon: 'globe' }
	];

	onMount(async () => {
		// Refresh the user/org from the server so suspension and trial state
		// reflect without re-login
		try {
			const fresh = await authAPI.me();
			currentUser.set(fresh);
		} catch {}

		try {
			const [convs, stats] = await Promise.all([
				conversationsAPI.getAll(),
				statsAPI.getDashboard()
			]);
			conversations.set(convs);
			dashboardStats.set(stats);
		} catch (error) {
			console.error('Error loading dashboard:', error);
		} finally {
			loading = false;
		}

		checkImpersonation();

		// Real-time socket (authenticated, org-scoped)
		rtConnect();

		// Mark online & start heartbeat
		try {
			await teamAPI.setMyPresence('online');
			presenceStatus = 'online';
		} catch {}

		await loadMentions();

		// Heartbeat every 60s; reload mentions every 90s
		presenceTimer = setInterval(async () => {
			try { await teamAPI.heartbeat(); } catch {}
		}, 60000);
		setInterval(loadMentions, 90000);
	});

	onDestroy(() => {
		if (presenceTimer) clearInterval(presenceTimer);
	});

	async function loadMentions() {
		try {
			const res = await teamAPI.listMentions(false);
			mentions = res.items || [];
			unreadMentions = res.unread_count || 0;
		} catch {}
	}

	async function changePresence(status) {
		presenceStatus = status;
		try {
			await teamAPI.setMyPresence(status, presenceMessage || null);
		} catch (e) {
			console.error('No se pudo actualizar presencia', e);
		}
	}

	async function toggleMentions() {
		mentionsOpen = !mentionsOpen;
		userMenuOpen = false;
		if (mentionsOpen && unreadMentions > 0) {
			try {
				await teamAPI.markAllMentionsRead();
				unreadMentions = 0;
			} catch {}
		}
	}

	function openMention(m) {
		mentionsOpen = false;
		// Navigate to conversations and (optionally) select
		setView('conversations');
		// Best-effort: try to select the linked conversation
		const target = ($conversations || []).find(c => c.id === m.conversation_id);
		if (target) selectedConversation.set(target);
	}

	function setView(view) {
		activeView = view;
		selectedConversation.set(null);
		userMenuOpen = false;
		mobileNavOpen = false; // close the mobile drawer after navigating
	}

	async function handleLogout() {
		rtDisconnect();
		try { await authAPI.logout(); } catch {}
		currentUser.set(null);
		window.location.reload();
	}

	function getBadge(item, stats) {
		if (!item.badgeKey) return null;
		const v = stats?.[item.badgeKey];
		return v && v > 0 ? v : null;
	}

	const viewTitles = {
		home: 'Inicio',
		leads: 'Leads',
		conversations: 'Bandeja de entrada',
		contacts: 'Contactos',
		labels: 'Etiquetas',
		'ai-agents': 'AI Agents',
		campaigns: 'Campañas de WhatsApp',
		flows: 'Flujos (Salesbot)',
		chatbots: 'Chatbots',
		knowledge: 'Base de conocimiento',
		automations: 'Automatizaciones',
		'ai-providers': 'Proveedores IA',
		team: 'Equipo',
		channels: 'Canales',
		superadmin: 'Super Admin',
		users: 'Usuarios',
		organizations: 'Organizaciones',
		settings: 'Ajustes',
		help: 'Centro de Ayuda',
		tutorials: 'Tutoriales'
	};
</script>

{#if impersonating}
	<div class="imp-banner">
		<Icon name="alert-circle" size={14} />
		<span>Estás impersonando <b>{impersonating.org_name}</b></span>
		<span class="muted">Termina en {Math.max(0, Math.round((impersonating.expires_at - Date.now()) / 60000))} min</span>
		<button class="exit-imp" onclick={exitImpersonation}>Salir</button>
	</div>
{/if}
{#if $currentUser && $currentUser.email_verified === false && !verifyBannerDismissed}
	<div class="verify-banner">
		<Icon name="mail" size={14} />
		<span>Confirma tu correo <b>{$currentUser.email}</b> para asegurar tu cuenta.</span>
		<button class="exit-imp" onclick={resendVerification} disabled={resendingVerify}>
			{resendingVerify ? 'Enviando...' : 'Reenviar correo'}
		</button>
		<button class="verify-dismiss" onclick={() => (verifyBannerDismissed = true)} aria-label="Cerrar">
			<Icon name="x" size={13} />
		</button>
	</div>
{/if}
{#if orgSuspended}
	<div class="locked-screen">
		<div class="locked-card">
			<div class="locked-icon"><Icon name="lock" size={28} /></div>
			{#if userOrg?.suspended_reason === 'trial_expired'}
				<h2>Tu prueba gratuita terminó</h2>
				<p>Esperamos que Mangu te haya sido útil estos 14 días. Para seguir usando tu cuenta —tus datos están intactos— activa un paquete con tu asesor.</p>
				<p class="locked-sinpe">Planes desde <b>₡14.850/mes</b>. Paga por SINPE Móvil al <b>8678-9960</b> y envía el comprobante por WhatsApp.</p>
			{:else}
				<h2>Cuenta suspendida</h2>
				<p>{userOrg?.suspended_reason || 'Contacta a tu asesor para reactivar tu cuenta.'}</p>
			{/if}
			<div class="locked-actions">
				<a class="btn btn-primary" href="https://wa.me/50686789960?text=Hola,%20quiero%20activar%20mi%20cuenta%20de%20Mangu"
					target="_blank" rel="noopener noreferrer">
					<Icon name="phone" size={15} /> Hablar con un asesor
				</a>
				<button class="btn btn-secondary" onclick={handleLogout}>Cerrar sesión</button>
			</div>
		</div>
	</div>
{:else}
{#if trialDaysLeft !== null && trialDaysLeft <= 3}
	<div class="trial-banner">
		<Icon name="alert-circle" size={14} />
		<span>Tu prueba gratuita termina en <b>{trialDaysLeft === 0 ? 'menos de un día' : `${trialDaysLeft} día${trialDaysLeft === 1 ? '' : 's'}`}</b>. Activa un paquete para no perder acceso.</span>
		<a class="exit-imp" href="https://wa.me/50686789960?text=Hola,%20quiero%20activar%20mi%20paquete%20de%20Mangu"
			target="_blank" rel="noopener noreferrer">Activar</a>
	</div>
{/if}
<div
	class="app-shell"
	class:collapsed
	class:has-imp={impersonating}
	class:nav-open={mobileNavOpen}
	class:chat-open={activeView === 'conversations' && $selectedConversation}
>
	<!-- Mobile drawer backdrop -->
	<button
		class="nav-backdrop"
		aria-label="Cerrar menú"
		onclick={() => (mobileNavOpen = false)}
	></button>
	<!-- ========== Sidebar ========== -->
	<aside class="sidebar">
		<div class="sidebar-brand">
			<div class="brand-mark">M</div>
			{#if !collapsed}<span class="brand-name">Mangu</span>{/if}
			<button
				class="collapse-btn"
				onclick={() => (collapsed = !collapsed)}
				aria-label="Toggle sidebar"
			>
				<Icon name={collapsed ? 'chevron-right' : 'chevron-left'} size={16} />
			</button>
		</div>

		<nav class="sidebar-nav">
			<ul class="nav-group">
				{#each navMain as item}
					{@const badge = getBadge(item, $dashboardStats)}
					<li>
						<button
							class="nav-item"
							class:active={activeView === item.id}
							onclick={() => setView(item.id)}
						>
							<Icon name={item.icon} size={18} />
							{#if !collapsed}<span class="nav-label">{item.label}</span>{/if}
							{#if badge && !collapsed}<span class="nav-badge">{badge}</span>{/if}
							{#if badge && collapsed}<span class="nav-dot"></span>{/if}
							{#if collapsed}<span class="nav-tooltip">{item.label}</span>{/if}
						</button>
					</li>
				{/each}
			</ul>

			{#if !collapsed}<div class="nav-section-title">IA & Automatización</div>{:else}<div class="nav-divider"></div>{/if}
			<ul class="nav-group">
				{#each visibleNavAI as item}
					<li>
						<button
							class="nav-item"
							class:active={activeView === item.id}
							onclick={() => setView(item.id)}
						>
							<Icon name={item.icon} size={18} />
							{#if !collapsed}<span class="nav-label">{item.label}</span>{/if}
							{#if collapsed}<span class="nav-tooltip">{item.label}</span>{/if}
						</button>
					</li>
				{/each}
			</ul>

			{#if !collapsed}<div class="nav-section-title">Sistema</div>{:else}<div class="nav-divider"></div>{/if}
			<ul class="nav-group">
				{#each navSystem as item}
					<li>
						<button
							class="nav-item"
							class:active={activeView === item.id}
							onclick={() => setView(item.id)}
						>
							<Icon name={item.icon} size={18} />
							{#if !collapsed}<span class="nav-label">{item.label}</span>{/if}
							{#if collapsed}<span class="nav-tooltip">{item.label}</span>{/if}
						</button>
					</li>
				{/each}
				{#if $currentUser?.role === 'admin' || $currentUser?.role === 'super_admin'}
					<li>
						<button
							class="nav-item"
							class:active={activeView === 'users'}
							onclick={() => setView('users')}
						>
							<Icon name="user" size={18} />
							{#if !collapsed}<span class="nav-label">Usuarios</span>{/if}
							{#if collapsed}<span class="nav-tooltip">Usuarios</span>{/if}
						</button>
					</li>
				{/if}
				{#if $currentUser?.role === 'super_admin'}
					<li>
						<button
							class="nav-item nav-superadmin"
							class:active={activeView === 'superadmin'}
							onclick={() => setView('superadmin')}
						>
							<Icon name="shield" size={18} />
							{#if !collapsed}<span class="nav-label">Super Admin</span>{/if}
							{#if collapsed}<span class="nav-tooltip">Super Admin</span>{/if}
						</button>
					</li>
				{/if}
			</ul>
		</nav>

		<div class="sidebar-foot">
			<button
				class="nav-item"
				class:active={activeView === 'tutorials'}
				onclick={() => setView('tutorials')}
			>
				<Icon name="book-open" size={18} />
				{#if !collapsed}<span class="nav-label">Tutoriales</span>{/if}
				{#if collapsed}<span class="nav-tooltip">Tutoriales</span>{/if}
			</button>
			<button
				class="nav-item"
				class:active={activeView === 'help'}
				onclick={() => setView('help')}
			>
				<Icon name="help-circle" size={18} />
				{#if !collapsed}<span class="nav-label">Centro de Ayuda</span>{/if}
				{#if collapsed}<span class="nav-tooltip">Centro de Ayuda</span>{/if}
			</button>
			<button
				class="nav-item"
				class:active={activeView === 'settings'}
				onclick={() => setView('settings')}
			>
				<Icon name="settings" size={18} />
				{#if !collapsed}<span class="nav-label">Ajustes</span>{/if}
				{#if collapsed}<span class="nav-tooltip">Ajustes</span>{/if}
			</button>
		</div>
	</aside>

	<!-- ========== Main column ========== -->
	<div class="main">
		<!-- Topbar -->
		<header class="topbar">
			<div class="topbar-left">
				<button
					class="hamburger"
					aria-label="Abrir menú"
					onclick={() => { mobileNavOpen = true; collapsed = false; }}
				>
					<Icon name="menu" size={20} />
				</button>
				<h1 class="page-title">{viewTitles[activeView] ?? ''}</h1>
			</div>

			<div class="topbar-search">
				<Icon name="search" size={16} class="search-icon" />
				<input
					type="text"
					placeholder="Buscar contactos, conversaciones, leads..."
					bind:value={searchQuery}
					oninput={onSearchInput}
					onfocus={() => { if (searchQuery.length >= 2) searchOpen = true; }}
					onblur={() => setTimeout(() => (searchOpen = false), 180)}
				/>
				<kbd class="kbd">⌘K</kbd>
				{#if searchOpen && searchResults}
					<div class="search-results">
						{#if searchResults.error}
							<div class="search-empty">Error: {searchResults.error}</div>
						{:else if !(searchResults.contacts?.length || searchResults.conversations?.length || searchResults.leads?.length)}
							<div class="search-empty">Sin resultados</div>
						{:else}
							{#if searchResults.conversations?.length}
								<div class="search-section">
									<div class="search-section-title">Conversaciones</div>
									{#each searchResults.conversations as c}
										<button class="search-item" onmousedown={() => pickSearchConversation(c)}>
											<Icon name="inbox" size={14} />
											<span class="si-title">{c.contact?.name || 'Sin contacto'}{c.subject ? ' · ' + c.subject : ''}</span>
										</button>
									{/each}
								</div>
							{/if}
							{#if searchResults.contacts?.length}
								<div class="search-section">
									<div class="search-section-title">Contactos</div>
									{#each searchResults.contacts as ct}
										<button class="search-item" onmousedown={pickSearchContact}>
											<Icon name="users" size={14} />
											<span class="si-title">{ct.name}{ct.company ? ' · ' + ct.company : ''}</span>
										</button>
									{/each}
								</div>
							{/if}
							{#if searchResults.leads?.length}
								<div class="search-section">
									<div class="search-section-title">Leads</div>
									{#each searchResults.leads as l}
										<button class="search-item" onmousedown={pickSearchLead}>
											<Icon name="kanban" size={14} />
											<span class="si-title">{l.title}</span>
										</button>
									{/each}
								</div>
							{/if}
						{/if}
					</div>
				{/if}
			</div>

			<div class="topbar-right">
				<div class="bell-wrap">
					<button class="icon-btn" aria-label="Notificaciones" onclick={toggleMentions}>
						<Icon name="bell" size={18} />
						{#if unreadMentions > 0}
							<span class="badge-count">{unreadMentions > 9 ? '9+' : unreadMentions}</span>
						{/if}
					</button>
					{#if mentionsOpen}
						<div class="mentions-dropdown" role="menu">
							<div class="mentions-head">
								<h4>Menciones y handoffs</h4>
								<button class="link-btn" onclick={loadMentions} aria-label="Refrescar">
									<Icon name="refresh" size={13} />
								</button>
							</div>
							{#if !mentions.length}
								<div class="mentions-empty">
									<Icon name="bell" size={20} />
									<p>Sin menciones por ahora</p>
								</div>
							{:else}
								<ul class="mentions-list">
									{#each mentions as m}
										<li>
											<button class="mention-item" class:unread={!m.read} onclick={() => openMention(m)}>
												<div class="m-avatar">{(m.by_user?.name || '?').charAt(0)}</div>
												<div class="m-text">
													<div class="m-by">
														<b>{m.by_user?.name || 'Sistema'}</b>
														<span class="m-time">{new Date(m.created_at).toLocaleString('es', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
													</div>
													<div class="m-excerpt">{m.note_excerpt || '(sin extracto)'}</div>
												</div>
											</button>
										</li>
									{/each}
								</ul>
							{/if}
						</div>
					{/if}
				</div>
				<div class="bell-wrap">
					<button class="icon-btn" aria-label="Ayuda" onclick={() => { helpOpen = !helpOpen; mentionsOpen = false; userMenuOpen = false; }}>
						<Icon name="help-circle" size={18} />
					</button>
					{#if helpOpen}
						<div class="mentions-dropdown" role="menu" style="width: 280px">
							<div class="mentions-head">
								<h4>Centro de ayuda</h4>
								<button class="link-btn" onclick={() => (helpOpen = false)} aria-label="Cerrar">
									<Icon name="x" size={13} />
								</button>
							</div>
							<div style="padding: 12px 14px; display: flex; flex-direction: column; gap: 8px;">
								<a class="dropdown-item" href="https://github.com/olidealer/mangu" target="_blank" rel="noopener noreferrer">
									<Icon name="book-open" size={16} /> Documentación
								</a>
								<a class="dropdown-item" href="https://wa.me/50686789960" target="_blank" rel="noopener noreferrer">
									<Icon name="phone" size={16} /> Hablar con soporte
								</a>
								<button class="dropdown-item" onclick={() => { helpOpen = false; setView('settings'); }}>
									<Icon name="settings" size={16} /> Ajustes de cuenta
								</button>
								<div style="font-size: 11.5px; color: var(--text-3); margin-top: 6px; padding: 0 8px; line-height: 1.5;">
									Tip: usa <kbd class="kbd">⌘K</kbd> para buscar conversaciones y contactos rápido.
								</div>
							</div>
						</div>
					{/if}
				</div>

				<div class="user-menu">
					<button
						class="user-trigger"
						onclick={() => { userMenuOpen = !userMenuOpen; mentionsOpen = false; }}
						aria-haspopup="true"
						aria-expanded={userMenuOpen}
					>
						<div class="avatar-wrap">
							<div class="avatar">{$currentUser?.name?.charAt(0) ?? '?'}</div>
							<span class="presence-dot" style="background: {PRESENCE_OPTS.find(o => o.k === presenceStatus)?.color}"></span>
						</div>
						<div class="user-meta">
							<span class="user-name">{$currentUser?.name ?? 'Usuario'}</span>
							<span class="user-role">{$currentUser?.role ?? ''}</span>
						</div>
						<Icon name="chevron-down" size={14} />
					</button>

					{#if userMenuOpen}
						<div class="user-dropdown" role="menu">
							<div class="dropdown-head">
								<div class="avatar">{$currentUser?.name?.charAt(0) ?? '?'}</div>
								<div>
									<div class="user-name">{$currentUser?.name}</div>
									<div class="user-email">{$currentUser?.email ?? ''}</div>
								</div>
							</div>
							<div class="presence-block">
								<div class="presence-title">Mi estado</div>
								{#each PRESENCE_OPTS as opt}
									<button
										class="dropdown-item presence-item"
										class:active={presenceStatus === opt.k}
										onclick={() => changePresence(opt.k)}
									>
										<span class="status-dot" style="background:{opt.color}"></span>
										<span>{opt.label}</span>
										{#if presenceStatus === opt.k}
											<Icon name="check" size={14} />
										{/if}
									</button>
								{/each}
							</div>
							<div class="divider"></div>
							<button class="dropdown-item" onclick={() => setView('team')}>
								<Icon name="users" size={16} />
								<span>Equipo</span>
							</button>
							<button class="dropdown-item" onclick={() => setView('settings')}>
								<Icon name="settings" size={16} />
								<span>Ajustes</span>
							</button>
							<button class="dropdown-item danger" onclick={handleLogout}>
								<Icon name="log-out" size={16} />
								<span>Cerrar sesión</span>
							</button>
						</div>
					{/if}
				</div>
			</div>
		</header>

		<!-- Content -->
		<section class="view">
			{#if loading}
				<div class="loading-screen">
					<div class="spinner"></div>
					<p>Cargando...</p>
				</div>
			{:else if activeView === 'home'}
				<Home onNavigate={setView} />
			{:else if activeView === 'leads'}
				<Leads />
			{:else if activeView === 'conversations'}
				<div class="conversations-layout" class:has-detail={$selectedConversation}>
					<Conversations />
					{#if $selectedConversation}
						<ConversationDetail />
					{/if}
				</div>
			{:else if activeView === 'contacts'}
				<Contacts />
			{:else if activeView === 'labels'}
				<Labels />
			{:else if activeView === 'ai-agents'}
				<AIAgents />
			{:else if activeView === 'campaigns'}
				<Campaigns />
			{:else if activeView === 'flows'}
				<Flows />
			{:else if activeView === 'chatbots'}
				<Chatbots />
			{:else if activeView === 'knowledge'}
				<KnowledgeBase />
			{:else if activeView === 'automations'}
				<Automations />
			{:else if activeView === 'ai-providers'}
				<AIProviders />
			{:else if activeView === 'team'}
				<Team />
			{:else if activeView === 'channels'}
				<Channels />
			{:else if activeView === 'users'}
				<Users currentUser={$currentUser} />
			{:else if activeView === 'organizations'}
				<Organizations />
			{:else if activeView === 'superadmin'}
				<SuperAdminPanel />
			{:else if activeView === 'settings'}
				<Settings />
			{:else if activeView === 'help'}
				<Help onNavigate={setView} />
			{:else if activeView === 'tutorials'}
				<Tutorials onNavigate={setView} />
			{/if}
		</section>
	</div>
</div>
{/if}

<style>
	.app-shell {
		display: flex;
		height: 100vh;
		height: 100dvh; /* visible viewport on mobile (keyboard/browser chrome) */
		background: var(--surface-1);
		overflow: hidden;
	}
	.app-shell.has-imp { height: calc(100vh - 36px); height: calc(100dvh - 36px); }

	/* Hamburger + drawer backdrop: hidden on desktop, shown on mobile */
	.hamburger {
		display: none;
		width: 38px; height: 38px;
		align-items: center; justify-content: center;
		border-radius: 8px;
		color: var(--text-2);
		flex-shrink: 0;
	}
	.hamburger:hover { background: var(--surface-1); color: var(--text-1); }
	.nav-backdrop { display: none; }

	.imp-banner {
		display: flex; align-items: center; gap: 10px;
		padding: 8px 16px;
		background: linear-gradient(135deg, var(--warning), #f97316);
		color: #fff;
		font-size: 12.5px;
		font-weight: 600;
		height: 36px;
	}
	.imp-banner :global(svg) { color: #fff; }
	.imp-banner .muted { color: rgba(255,255,255,0.85); margin-left: auto; font-weight: 500; }
	.exit-imp {
		background: rgba(0,0,0,0.2);
		color: #fff;
		padding: 4px 12px;
		border-radius: 6px;
		font-weight: 600;
		font-size: 12px;
	}
	.exit-imp:hover { background: rgba(0,0,0,0.35); }

	.verify-banner {
		display: flex; align-items: center; gap: 10px;
		padding: 8px 16px;
		background: var(--accent-soft);
		color: var(--text-1);
		font-size: 12.5px;
		border-bottom: 1px solid var(--border-1);
	}
	.verify-banner :global(svg) { color: var(--accent); }
	.verify-banner .exit-imp { background: var(--accent); margin-left: auto; }
	.verify-banner .exit-imp:hover { background: var(--accent-hover); }
	.verify-dismiss {
		width: 24px; height: 24px;
		display: flex; align-items: center; justify-content: center;
		border-radius: 6px;
		color: var(--text-3);
	}
	.verify-dismiss:hover { background: var(--surface-2); color: var(--text-1); }

	/* Trial */
	.trial-banner {
		display: flex; align-items: center; gap: 10px;
		padding: 8px 16px;
		background: var(--warning-soft);
		color: #92400e;
		font-size: 12.5px;
		border-bottom: 1px solid var(--border-1);
	}
	.trial-banner .exit-imp {
		margin-left: auto;
		background: var(--warning);
		color: #fff;
		text-decoration: none;
	}
	.locked-screen {
		height: 100vh;
		display: flex; align-items: center; justify-content: center;
		background: var(--surface-1);
		padding: 24px;
	}
	.locked-card {
		background: var(--surface-0);
		border: 1px solid var(--border-1);
		border-radius: var(--radius-lg);
		box-shadow: var(--shadow-lg);
		max-width: 460px;
		padding: 40px;
		text-align: center;
	}
	.locked-icon {
		width: 60px; height: 60px;
		margin: 0 auto 18px;
		border-radius: 50%;
		background: var(--warning-soft);
		color: #b45309;
		display: flex; align-items: center; justify-content: center;
	}
	.locked-card h2 { font-size: 20px; margin-bottom: 10px; }
	.locked-card p { color: var(--text-2); font-size: 14px; line-height: 1.6; margin-bottom: 22px; }
	.locked-card .locked-sinpe {
		font-size: 13px;
		background: var(--accent-soft, rgba(33, 150, 243, 0.08));
		border-radius: 8px;
		padding: 10px 14px;
		margin-top: -10px;
	}
	.locked-actions { display: flex; gap: 10px; justify-content: center; flex-wrap: wrap; }

	.nav-superadmin.active {
		background: rgba(168, 85, 247, 0.12);
		color: #9333ea !important;
	}
	.nav-superadmin.active::before { background: #a855f7 !important; }

	/* ===== Sidebar (Kommo-style light icon rail) ===== */
	.sidebar {
		width: var(--sidebar-width-expanded);
		background: var(--sidebar-bg);
		color: var(--sidebar-text);
		display: flex;
		flex-direction: column;
		flex-shrink: 0;
		transition: width var(--transition);
		border-right: 1px solid var(--sidebar-border);
	}
	.app-shell.collapsed .sidebar { width: var(--sidebar-width-collapsed); }

	.sidebar-brand {
		height: var(--topbar-height);
		display: flex;
		align-items: center;
		gap: 10px;
		padding: 0 14px;
		border-bottom: 1px solid var(--sidebar-border);
		position: relative;
	}
	.brand-mark {
		width: 32px; height: 32px;
		border-radius: 8px;
		background: linear-gradient(135deg, var(--accent) 0%, var(--accent-hover) 100%);
		display: flex; align-items: center; justify-content: center;
		font-weight: 700; color: #fff; font-size: 16px;
		flex-shrink: 0;
	}
	.brand-name {
		font-weight: 700;
		font-size: 16px;
		color: var(--text-1);
		letter-spacing: -0.01em;
	}
	.collapse-btn {
		margin-left: auto;
		width: 26px; height: 26px;
		border-radius: 6px;
		display: flex; align-items: center; justify-content: center;
		color: var(--sidebar-text);
		transition: background var(--transition-fast), color var(--transition-fast);
	}
	.collapse-btn:hover {
		background: var(--sidebar-bg-hover);
		color: var(--text-1);
	}
	.app-shell.collapsed .collapse-btn { margin-left: 0; }

	.sidebar-nav {
		flex: 1;
		padding: 10px 8px;
		overflow-y: auto;
	}
	/* Collapsed rail: let tooltips overflow the 64px column */
	.app-shell.collapsed .sidebar-nav { overflow: visible; }
	.nav-group {
		list-style: none;
		margin: 0; padding: 0;
		display: flex; flex-direction: column; gap: 2px;
	}
	.nav-section-title {
		font-size: 11px;
		font-weight: 600;
		color: var(--text-3);
		text-transform: uppercase;
		letter-spacing: 0.06em;
		padding: 14px 12px 6px;
	}
	.nav-divider {
		height: 1px;
		background: var(--sidebar-border);
		margin: 10px 6px;
	}
	.nav-item {
		display: flex; align-items: center;
		gap: 10px;
		width: 100%;
		padding: 8px 10px;
		border-radius: 8px;
		color: var(--sidebar-text);
		font-size: 13.5px;
		font-weight: 500;
		text-align: left;
		transition: background var(--transition-fast), color var(--transition-fast);
		position: relative;
	}
	.app-shell.collapsed .nav-item {
		justify-content: center;
		padding: 10px;
	}
	.nav-item:hover {
		background: var(--sidebar-bg-hover);
		color: var(--text-1);
	}
	.nav-item.active {
		background: var(--sidebar-active-bg);
		color: var(--sidebar-text-active);
	}
	.nav-item.active::before {
		content: '';
		position: absolute;
		left: -8px; top: 50%; transform: translateY(-50%);
		width: 3px; height: 18px;
		background: var(--accent);
		border-radius: 2px;
	}
	.nav-label { flex: 1; }
	.nav-badge {
		background: var(--accent);
		color: #fff;
		font-size: 11px; font-weight: 700;
		padding: 1px 7px;
		border-radius: 9999px;
		min-width: 20px;
		text-align: center;
	}
	.nav-dot {
		position: absolute; top: 8px; right: 8px;
		width: 7px; height: 7px;
		background: var(--accent);
		border-radius: 50%;
	}
	/* Flyout label shown on hover when the rail is collapsed (Kommo style) */
	.nav-tooltip {
		position: absolute;
		left: calc(100% + 10px);
		top: 50%;
		transform: translateY(-50%);
		background: var(--surface-0);
		color: var(--text-1);
		font-size: 12.5px;
		font-weight: 600;
		padding: 5px 10px;
		border-radius: 7px;
		border: 1px solid var(--border-1);
		box-shadow: var(--shadow-md);
		white-space: nowrap;
		opacity: 0;
		pointer-events: none;
		transition: opacity var(--transition-fast);
		z-index: 80;
	}
	.nav-item:hover .nav-tooltip,
	.nav-item:focus-visible .nav-tooltip { opacity: 1; }
	.sidebar-foot {
		padding: 10px 8px;
		border-top: 1px solid var(--sidebar-border);
	}

	/* ===== Main ===== */
	.main {
		flex: 1;
		display: flex;
		flex-direction: column;
		min-width: 0;
	}

	.topbar {
		height: var(--topbar-height);
		display: flex;
		align-items: center;
		gap: 16px;
		padding: 0 20px;
		background: var(--surface-0);
		border-bottom: 1px solid var(--border-1);
		flex-shrink: 0;
	}
	.topbar-left { min-width: 0; display: flex; align-items: center; gap: 8px; }
	.page-title {
		font-size: 16px;
		font-weight: 600;
		color: var(--text-1);
		margin: 0;
	}
	.topbar-search {
		flex: 1;
		max-width: 480px;
		position: relative;
		display: flex;
		align-items: center;
	}
	.topbar-search :global(.search-icon) {
		position: absolute;
		left: 12px;
		color: var(--text-3);
		pointer-events: none;
	}
	.topbar-search input {
		padding: 8px 12px 8px 36px;
		background: var(--surface-1);
		border: 1px solid transparent;
		font-size: 13.5px;
	}
	.topbar-search input:focus {
		background: var(--surface-0);
		border-color: var(--accent);
	}
	.kbd {
		position: absolute;
		right: 10px;
		font-family: var(--font-mono);
		font-size: 11px;
		color: var(--text-3);
		background: var(--surface-2);
		padding: 2px 6px;
		border-radius: 4px;
		border: 1px solid var(--border-2);
	}

	.search-results {
		position: absolute;
		top: calc(100% + 6px);
		left: 0; right: 0;
		max-height: 380px;
		overflow-y: auto;
		background: var(--surface-0);
		border: 1px solid var(--border-1);
		border-radius: 10px;
		box-shadow: var(--shadow-lg);
		z-index: 60;
	}
	.search-section { padding: 4px 0; }
	.search-section + .search-section { border-top: 1px solid var(--border-1); }
	.search-section-title {
		padding: 6px 12px;
		font-size: 10.5px;
		font-weight: 700;
		color: var(--text-3);
		text-transform: uppercase;
		letter-spacing: 0.06em;
	}
	.search-item {
		display: flex; align-items: center; gap: 8px;
		width: 100%;
		padding: 8px 12px;
		text-align: left;
		font-size: 13px;
		color: var(--text-1);
		transition: background var(--transition-fast);
	}
	.search-item:hover { background: var(--surface-1); }
	.si-title { flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
	.search-empty {
		padding: 18px;
		text-align: center;
		color: var(--text-3);
		font-size: 12.5px;
	}

	.topbar-right {
		margin-left: auto;
		display: flex;
		align-items: center;
		gap: 4px;
	}
	.icon-btn {
		position: relative;
		width: 36px; height: 36px;
		border-radius: 8px;
		display: flex; align-items: center; justify-content: center;
		color: var(--text-2);
		transition: background var(--transition-fast), color var(--transition-fast);
	}
	.icon-btn:hover { background: var(--surface-2); color: var(--text-1); }

	.badge-count {
		position: absolute; top: 4px; right: 4px;
		min-width: 16px; height: 16px;
		padding: 0 4px;
		display: flex; align-items: center; justify-content: center;
		background: var(--danger);
		color: #fff;
		font-size: 9.5px;
		font-weight: 700;
		border-radius: 9999px;
		border: 2px solid var(--surface-0);
		line-height: 1;
	}

	.bell-wrap { position: relative; }
	.mentions-dropdown {
		position: absolute;
		right: 0; top: calc(100% + 6px);
		width: 360px;
		max-height: 480px;
		display: flex; flex-direction: column;
		background: var(--surface-0);
		border: 1px solid var(--border-1);
		border-radius: 10px;
		box-shadow: var(--shadow-lg);
		z-index: 60;
		overflow: hidden;
	}
	.mentions-head {
		display: flex; align-items: center; justify-content: space-between;
		padding: 12px 14px;
		border-bottom: 1px solid var(--border-1);
	}
	.mentions-head h4 { font-size: 13px; }
	.link-btn {
		width: 26px; height: 26px;
		display: flex; align-items: center; justify-content: center;
		border-radius: 6px;
		color: var(--text-3);
	}
	.link-btn:hover { background: var(--surface-2); color: var(--text-1); }
	.mentions-empty {
		flex: 1;
		padding: 32px 16px;
		display: flex; flex-direction: column; align-items: center; gap: 6px;
		color: var(--text-3);
		font-size: 13px;
	}
	.mentions-list {
		list-style: none; padding: 6px; margin: 0;
		overflow-y: auto;
	}
	.mention-item {
		display: flex; gap: 10px;
		width: 100%;
		padding: 10px;
		border-radius: 8px;
		text-align: left;
		transition: background var(--transition-fast);
	}
	.mention-item:hover { background: var(--surface-1); }
	.mention-item.unread { background: var(--accent-soft); }
	.mention-item.unread:hover { background: var(--accent-soft); filter: brightness(0.97); }
	.m-avatar {
		width: 32px; height: 32px;
		border-radius: 50%;
		background: linear-gradient(135deg, var(--accent) 0%, var(--accent-hover) 100%);
		color: #fff;
		display: flex; align-items: center; justify-content: center;
		font-weight: 600;
		font-size: 13px;
		flex-shrink: 0;
	}
	.m-text { flex: 1; min-width: 0; }
	.m-by {
		display: flex; align-items: center; justify-content: space-between;
		font-size: 12px;
		color: var(--text-1);
	}
	.m-time { color: var(--text-3); font-size: 11px; }
	.m-excerpt {
		font-size: 12px;
		color: var(--text-2);
		line-height: 1.4;
		display: -webkit-box;
		-webkit-line-clamp: 2;
		-webkit-box-orient: vertical;
		overflow: hidden;
	}

	.user-menu { position: relative; margin-left: 4px; }
	.avatar-wrap { position: relative; flex-shrink: 0; }
	.presence-dot {
		position: absolute;
		bottom: -2px; right: -2px;
		width: 11px; height: 11px;
		border-radius: 50%;
		border: 2px solid var(--surface-0);
	}
	.presence-block {
		padding: 4px;
		border-bottom: 1px solid var(--border-1);
	}
	.presence-title {
		font-size: 10.5px;
		font-weight: 600;
		text-transform: uppercase;
		letter-spacing: 0.05em;
		color: var(--text-3);
		padding: 6px 10px 4px;
	}
	.presence-item.active { background: var(--accent-soft); color: var(--accent); }
	.status-dot {
		display: inline-block;
		width: 8px; height: 8px;
		border-radius: 50%;
		flex-shrink: 0;
	}
	.divider { height: 1px; background: var(--border-1); margin: 4px 0; }
	.user-trigger {
		display: flex; align-items: center; gap: 8px;
		padding: 6px 8px 6px 6px;
		border-radius: 8px;
		color: var(--text-1);
		transition: background var(--transition-fast);
	}
	.user-trigger:hover { background: var(--surface-2); }
	.avatar {
		width: 32px; height: 32px;
		border-radius: 50%;
		background: linear-gradient(135deg, var(--accent) 0%, var(--accent-hover) 100%);
		display: flex; align-items: center; justify-content: center;
		color: #fff; font-weight: 600; font-size: 13px;
		flex-shrink: 0;
	}
	.user-meta {
		display: flex; flex-direction: column; align-items: flex-start;
		line-height: 1.2;
		text-align: left;
	}
	.user-name { font-size: 13px; font-weight: 600; color: var(--text-1); }
	.user-role {
		font-size: 11px; color: var(--text-3);
		text-transform: capitalize;
	}
	.user-email { font-size: 12px; color: var(--text-3); }

	.user-dropdown {
		position: absolute;
		right: 0; top: calc(100% + 6px);
		width: 240px;
		background: var(--surface-0);
		border: 1px solid var(--border-1);
		border-radius: 10px;
		box-shadow: var(--shadow-lg);
		padding: 6px;
		z-index: 50;
	}
	.dropdown-head {
		display: flex; gap: 10px; align-items: center;
		padding: 10px;
		border-bottom: 1px solid var(--border-1);
		margin-bottom: 6px;
	}
	.dropdown-item {
		display: flex; align-items: center; gap: 10px;
		width: 100%;
		padding: 8px 10px;
		border-radius: 6px;
		font-size: 13.5px;
		color: var(--text-1);
		transition: background var(--transition-fast);
	}
	.dropdown-item:hover { background: var(--surface-1); }
	.dropdown-item.danger { color: var(--danger); }
	.dropdown-item.danger:hover { background: var(--danger-soft); }

	/* ===== View ===== */
	.view {
		flex: 1;
		overflow: auto;
		min-height: 0;
	}
	.conversations-layout {
		display: flex;
		height: 100%;
	}

	.loading-screen {
		display: flex; flex-direction: column;
		align-items: center; justify-content: center;
		height: 100%;
		gap: 14px;
		color: var(--text-3);
	}
	.spinner {
		width: 32px; height: 32px;
		border: 3px solid var(--border-1);
		border-top-color: var(--accent);
		border-radius: 50%;
		animation: spin 700ms linear infinite;
	}
	@keyframes spin { to { transform: rotate(360deg); } }

	/* Responsive */
	@media (max-width: 900px) {
		/* Inbox: show either the list or the open conversation, full width */
		.conversations-layout.has-detail > :global(.conversations-panel) { display: none; }
	}

	/* ===== Mobile (phones): WhatsApp-style off-canvas nav + full-bleed views ===== */
	@media (max-width: 768px) {
		.user-meta { display: none; }
		.topbar-search { display: none; }
		.page-title { font-size: 15px; font-weight: 600; }

		.hamburger { display: flex; }

		/* Sidebar becomes a slide-in drawer, hidden by default */
		.sidebar {
			position: fixed;
			top: 0; left: 0; bottom: 0;
			z-index: 60;
			width: 268px !important;       /* override the collapsed/expanded rail */
			transform: translateX(-100%);
			transition: transform var(--transition);
			box-shadow: var(--shadow-xl);
		}
		.app-shell.nav-open .sidebar { transform: translateX(0); }
		/* Always show labels inside the drawer, even if "collapsed" was set */
		.sidebar .nav-label,
		.sidebar .brand-name { display: inline !important; }
		.collapse-btn { display: none; }      /* not needed on mobile */
		.nav-tooltip { display: none !important; }

		.nav-backdrop {
			display: block;
			position: fixed; inset: 0;
			z-index: 55;
			border: none;
			background: rgba(15, 23, 42, 0.45);
			opacity: 0; pointer-events: none;
			transition: opacity var(--transition);
		}
		.app-shell.nav-open .nav-backdrop { opacity: 1; pointer-events: auto; }

		.topbar { padding: 0 12px; gap: 8px; height: 52px; }
		.topbar-right { gap: 2px; }

		/* Views: remove desktop breathing room so the chat is full-bleed */
		.conversations-layout { height: 100%; }

		/* When a chat is open, hand the whole screen to it (its own header has
		   the back button) — exactly like opening a WhatsApp conversation. */
		.app-shell.chat-open .topbar { display: none; }
	}
</style>
